/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import ModuleUtils from './ModuleUtils';
import DynamicSchemaUtils from './DynamicSchemaUtils';
import ReferenceResolver from './ReferenceResolver';
import InstanceType from './InstanceType';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/core/module/util/ModuleUtils (runtime) // getUseSpecRefs
//@import com/ibm/bi/platform/moser/common/utils/DynamicSchemaUtils (runtime) // createDataSourceOverride
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (runtime) // getSecondPart
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType

/**
 * Module relinker, used to relink a Module's UseSpec from one source to another.
 * @author  PASCALLacombe
 */
export default class ModuleRelinker extends BaseMoserObject{

	module = null;

	newSrcModule = null;



	/**
	 * Constructor
	 * @param {Module} module
	 * @param {Module} newSrcModule
	 */
	constructor(module, newSrcModule) {
		super();
		this.module = module;
		this.newSrcModule = newSrcModule;
	}

	/**
	 * Relink a source in a module
	 * @param {UseSpec} useSpec  : the useSpec to relink
	 * @param {UseSpecType} newSrcType  : the new source type (package, module, file, ...etc)
	 * @param {String} newSrcStoreId  : the new source module store id
	 * @param  newSrcAncestors: the ancestors of the useSpec
	 * @param  objectFactory: factory to create new module objects
	 * @return {UseSpec}  newly created UseSpec for the relinked source
	 */
	relinkSource(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory) {
		return this._doRelinkSource(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory);
	}

	/**
	 * Do the relink a source in a module, needs to be a separate call for translation
	 * @param {UseSpec} useSpec  : the useSpec to relink
	 * @param {UseSpecType} newSrcType  : the new source type (package, module, file, ...etc)
	 * @param {String} newSrcStoreId  : the new source module store id
	 * @param  newSrcAncestors: the ancestors of the useSpec
	 * @param  objectFactory: factory to create new module objects
	 * @return {UseSpec}  newly created UseSpec for the relinked source
	 */
	_doRelinkSource(useSpec, newSrcType, newSrcStoreId, newSrcAncestors, objectFactory) {
		var newUseSpec = objectFactory.createModuleUseSpec();
		newUseSpec.setIdentifier(useSpec.getIdentifier());
		newUseSpec.setType(newSrcType);
		newUseSpec.setStoreID(newSrcStoreId);
		newUseSpec.setSearchPath(null);
		if (newSrcAncestors && !(newSrcAncestors.length==0)) {
			Array.prototype.push.apply(newUseSpec.getAncestors(), newSrcAncestors);
		}
		newUseSpec.setDataCacheExpiry(useSpec.getDataCacheExpiry());
		var position = this.module.getUseSpec().indexOf(useSpec);
		this.module.removeUseSpecAt(position);
		this.module.addUseSpecAt(position, newUseSpec);
		return newUseSpec;
	}

	/**
	 * Relink identifiers in a module that refer to a particular useSpec so they use identifiers from newSrcModule.
	 * @param {UseSpec} useSpec  : the useSpec to relink identifiers for
	 */
	relinkIdentifiers(useSpec) {
		var refsToUseSpec = ModuleUtils.getUseSpecRefs(useSpec, this.module);
		this._relinkIdentifierRefs(refsToUseSpec, useSpec);
	}

	/**
	 * Relink data source override in a module that refer to a particular useSpec so they use the data source information from newSrcModule.
	 * @param {UseSpec} useSpec  : the useSpec to relink data source override for
	 */
	relinkDataSourceOverride(useSpec) {
		var dataSourceOverride = DynamicSchemaUtils.createDataSourceOverride(this.module, useSpec, this.newSrcModule);
		if (dataSourceOverride) {
			useSpec.setDataSourceOverride(dataSourceOverride);
		}
	}

	/**
	 * This method is called after a 'source relink' to relink identifier references. The refs are objects that have a identifier references to the 'original source' and newSrcModule is the module of the relinked 'target source'. The identifier reference resolution rules follow PL284: 1.	Matching identifier, no change 2.	If matching historical identifier, pick it 3.	No matching identifiers: i.	If new source only contains 1 identifier, pick it ii.	Find best matching identifier, score candidates by matching item identifiers iii.	If all candidates score equally poor, replace identifiers in the order of appearance, repeating the last identifier if necessary.
	 * @param {Array} refs  - All ObjectType's that have an identifier reference
	 * @param {UseSpec} newUseSpec  - The new useSpec to use
	 */
	_relinkIdentifierRefs(refs, newUseSpec) {
		if (!refs || refs.length==0) {
			return;
		}
		var identifiers = [];
		var identifierToObjectType = ModuleRelinker._identifierToObjectType(this.newSrcModule, identifiers);
		for (var __i_enFor0 = 0, __exp_enFor0 = refs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var obj = __exp_enFor0[__i_enFor0];
			var objType = obj.getObjectType();
			if ("QuerySubject" == objType) {
				ModuleRelinker._relinkQuerySubject(obj, newUseSpec, identifierToObjectType, identifiers);
			} else if ("Relationship" == objType) {
				ModuleRelinker._relinkRelationship(obj, newUseSpec, identifierToObjectType, identifiers);
			} else if ("Calculation" == objType) {
				ModuleRelinker._relinkCalculation(obj, newUseSpec, identifierToObjectType, identifiers);
			} else if ("Filter" == objType) {
				ModuleRelinker._relinkFilter(obj, newUseSpec, identifierToObjectType, identifiers);
			}
		}
	}

	/**
	 * Create an identifier to ObjectType map.
	 * @param {Module} module
	 * @param {Array} identifiers
	 * @return {Map}
	 */
	static _identifierToObjectType(module, identifiers) {
		var identifierToObjectType = {};
		var querySubjects = module.basicGetQuerySubject();
		if (querySubjects && !(querySubjects.length==0)) {
			for (var __i_enFor0 = 0, __exp_enFor0 = querySubjects, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				identifierToObjectType[qs.getIdentifier()] = qs;
				identifiers.push(qs.getIdentifier());
			}
		}
		var relationships = module.basicGetRelationship();
		if (relationships && !(relationships.length==0)) {
			for (var __i_enFor1 = 0, __exp_enFor1 = relationships, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var rel = __exp_enFor1[__i_enFor1];
				identifierToObjectType[rel.getIdentifier()] = rel;
				identifiers.push(rel.getIdentifier());
			}
		}
		var calculations = module.basicGetCalculation();
		if (calculations && !(calculations.length==0)) {
			for (var __i_enFor2 = 0, __exp_enFor2 = calculations, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var calc = __exp_enFor2[__i_enFor2];
				identifierToObjectType[calc.getIdentifier()] = calc;
				identifiers.push(calc.getIdentifier());
			}
		}
		var filters = module.basicGetFilter();
		if (filters && !(filters.length==0)) {
			for (var __i_enFor3 = 0, __exp_enFor3 = filters, __len_enFor3 = __exp_enFor3.length;
					__i_enFor3 < __len_enFor3; ++__i_enFor3) {
				var filter = __exp_enFor3[__i_enFor3];
				identifierToObjectType[filter.getIdentifier()] = filter;
				identifiers.push(filter.getIdentifier());
			}
		}
		return identifierToObjectType;
	}

	/**
	 * This method is called after a 'source relink' to relink a QuerySubject.
	 * @param {QuerySubject} qs  - The QuerySubject to relink
	 * @param {UseSpec} newUseSpec  - The new useSpec to use
	 * @param {Map} identifierToObjectType  - Map of candidates
	 * @param {Array} identifiers  - Candidate identifiers
	 */
	static _relinkQuerySubject(qs, newUseSpec, identifierToObjectType, identifiers) {
		var matchingObject;
		var allRefs = ModuleRelinker._buildQSRefList(qs);
		for (var __i_enFor0 = 0, __exp_enFor0 = allRefs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var ref = __exp_enFor0[__i_enFor0];
			matchingObject = identifierToObjectType[ref];
			if (matchingObject) {
				break;
			}
			matchingObject = identifierToObjectType[ref.toLowerCase()];
			if (matchingObject) {
				break;
			}
		}
		if (!matchingObject || !("QuerySubject" == matchingObject.getObjectType())) {
			matchingObject = ModuleRelinker._bestMatch(qs, identifierToObjectType, identifiers);
		}
		if (!matchingObject) {
			return;
		}
		while (!(qs.getRef().length==0)) {
			qs.removeRefAt(0);
		}
		qs.addRef(newUseSpec.getIdentifier() + "." + matchingObject.getIdentifier());
	}

	/**
	 * Build a list of QS reference identifiers.
	 * @param {QuerySubject} qs
	 * @return {Array}
	 */
	static _buildQSRefList(qs) {
		var refs = qs.basicGetRef();
		var originalRefs = qs.basicGetOriginalRef();
		var result = [];
		if (refs && !(refs.length==0)) {
			for (var __i_enFor0 = 0, __exp_enFor0 = refs, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var ref = __exp_enFor0[__i_enFor0];
				ModuleRelinker._addSecondPartIdentifier(result, ref);
			}
		}
		if (originalRefs && !(originalRefs.length==0)) {
			for (var __i_enFor1 = 0, __exp_enFor1 = originalRefs, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var ref = __exp_enFor1[__i_enFor1];
				ModuleRelinker._addSecondPartIdentifier(result, ref);
			}
		}
		ModuleRelinker._addSecondPartIdentifier(result, qs.getIdentifier());
		return result;
	}

	/**
	 * Add an second part identifier from a ref if present.
	 * @param {Array} result
	 * @param {String} ref
	 */
	static _addSecondPartIdentifier(result, ref) {
		var identifier = ReferenceResolver.getSecondPart(ref);
		if (identifier != null && !(identifier.length === 0)) {
			result.push(identifier);
		}
	}

	/**
	 * Find the best matching object.
	 * @param {ObjectType} obj
	 * @param {Map} identifierToObjectType
	 * @param {Array} identifiers
	 * @return {ObjectType}
	 */
	static _bestMatch(obj, identifierToObjectType, identifiers) {
		var candidates = [];
		var type = obj.getObjectType();
		for (var __i_enFor0 = 0, __exp_enFor0 = identifiers, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var id = __exp_enFor0[__i_enFor0];
			var candidate = identifierToObjectType[id];
			if (type == candidate.getObjectType()) {
				candidates.push(candidate);
			}
		}
		return candidates.length==0 ? null : candidates.length == 1 ? candidates[0] : ModuleRelinker._bestCandidate(obj, candidates);
	}

	/**
	 * Find the best candidate matching the obj.
	 * @param {ObjectType} obj
	 * @param {Array} candidates
	 * @return {ObjectType}
	 */
	static _bestCandidate(obj, candidates) {
		if (!("QuerySubject" == obj.getObjectType())) {
			return null;
		}
		var bestScore = 1;
		var querySubject = obj;
		var bestCandidate;
		var identifiers = ModuleRelinker._collectItemsIdentifiers(querySubject.basicGetItem());
		for (var __i_enFor0 = 0, __exp_enFor0 = candidates, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var c = __exp_enFor0[__i_enFor0];
			var candidate = c;
			var score = querySubject.getLabel() == candidate.getLabel() ? 1 : 0;
			score += ModuleRelinker._scoreCandidate(identifiers, ModuleRelinker._collectItemsIdentifiers(candidate.basicGetItem()));
			if (score > bestScore) {
				bestCandidate = candidate;
				bestScore = score;
			}
		}
		return bestCandidate;
	}

	/**
	 * Return all identifiers in a list of ItemType.
	 * @param {Array} items
	 * @return {Array}
	 */
	static _collectItemsIdentifiers(items) {
		var identifiers = [];
		if (!items || items.length==0) {
			return identifiers;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			if (item.getQueryItem()) {
				identifiers.push(item.getQueryItem().getIdentifier());
			} else if (item.getFilter()) {
				identifiers.push(item.getFilter().getIdentifier());
			} else if (item.getItemHierarchy()) {
				identifiers.push(item.getItemHierarchy().getIdentifier());
			} else if (item.getNamedSet()) {
				identifiers.push(item.getNamedSet().getIdentifier());
			} else if (item.getFolder()) {
				Array.prototype.push.apply(identifiers, ModuleRelinker._collectItemsIdentifiers(item.getFolder().basicGetItem()));
			}
		}
		return identifiers;
	}

	/**
	 * Score the number of matching identifiers.
	 * @param {Array} identifiers
	 * @param {Array} newIdentifiers
	 * @return {int}
	 */
	static _scoreCandidate(identifiers, newIdentifiers) {
		if (!newIdentifiers || newIdentifiers.length==0) {
			return 0;
		}
		var score = 0;
		for (var __i_enFor0 = 0, __exp_enFor0 = identifiers, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var identifier = __exp_enFor0[__i_enFor0];
			score += newIdentifiers.indexOf(identifier)> -1 ? 1 : 0;
		}
		return score;
	}

	/**
	 * This method is called after a 'source relink' to relink a Relationship.
	 * @param {Relationship} rel  - The Relationship to relink
	 * @param {UseSpec} newUseSpec  - The new useSpec to use
	 * @param {Map} identifierToObjectType  - Map of candidates
	 * @param {Array} identifiers  - Candidate identifiers
	 */
	static _relinkRelationship(rel, newUseSpec, identifierToObjectType, identifiers) {
		var originalRef = rel.getRef();
		var ref = ReferenceResolver.getSecondPart(originalRef);
		var matchingObject = identifierToObjectType[ref];
		if (InstanceType.MISSING === rel.getInstanceType()) {
			rel.setInstanceType(null);
			var props = rel.basicGetPropertyOverride();
			if (props && !(props.length==0)) {
				var index = props.indexOf("instanceType");
				if (index >= 0) {
					props.splice(index, 1);
				}
			}
		}
		if (!matchingObject || !("Relationship" == matchingObject.getObjectType())) {
			matchingObject = ModuleRelinker._bestMatch(rel, identifierToObjectType, identifiers);
		}
		if (!matchingObject) {
			return;
		}
		rel.setRef(newUseSpec.getIdentifier() + "." + matchingObject.getIdentifier());
	}

	/**
	 * This method is called after a 'source relink' to relink a Calculation.
	 * @param {Calculation} calc  - The Calculation to relink
	 * @param {UseSpec} newUseSpec  - The new useSpec to use
	 * @param {Map} identifierToObjectType  - Map of candidates
	 * @param {Array} identifiers  - Candidate identifiers
	 */
	static _relinkCalculation(calc, newUseSpec, identifierToObjectType, identifiers) {
		var originaRef = calc.getRef();
		var ref = ReferenceResolver.getSecondPart(originaRef);
		var matchingObject = identifierToObjectType[ref];
		if (!matchingObject || !("Calculation" == matchingObject.getObjectType())) {
			matchingObject = ModuleRelinker._bestMatch(calc, identifierToObjectType, identifiers);
		}
		if (!matchingObject) {
			return;
		}
		calc.setRef(newUseSpec.getIdentifier() + "." + matchingObject.getIdentifier());
	}

	/**
	 * This method is called after a 'source relink' to relink a Filter.
	 * @param {Filter} filter  - The Filter to relink
	 * @param {UseSpec} newUseSpec  - The new useSpec to use
	 * @param {Map} identifierToObjectType  - Map of candidates
	 * @param {Array} identifiers  - Candidate identifiers
	 */
	static _relinkFilter(filter, newUseSpec, identifierToObjectType, identifiers) {
		var originaRef = filter.getRef();
		var ref = ReferenceResolver.getSecondPart(originaRef);
		var matchingObject = identifierToObjectType[ref];
		if (!matchingObject || !("Filter" == matchingObject.getObjectType())) {
			matchingObject = ModuleRelinker._bestMatch(filter, identifierToObjectType, identifiers);
		}
		if (!matchingObject) {
			return;
		}
		filter.setIdentifier(newUseSpec.getIdentifier() + "." + matchingObject.getIdentifier());
	}
}


// WEBPACK FOOTER //
// ./src/generated/ModuleRelinker.js